"use client";
import {
  AudioTrack,
  useConnectionState,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  useVoiceAssistant,
} from "@livekit/components-react";
import { ConnectionState, LocalParticipant, Track } from "livekit-client";
import { X } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { FaMicrophone, FaSquareFull } from "react-icons/fa";
import { IoChatbubble } from "react-icons/io5";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import AgentAvatar from "./agent-avatar";
import { iChoiceProps } from "./choice";

const LiveKitRoomContent: FC<{
  isMuted: boolean;
  volume: number;
  callDuration: string;
  config: iChoiceProps["config"];
  onClose: () => void;
  onChatSelection: () => void;
  onEndCall: () => void;
  handleMicrophoneToggle: () => void;
  handleVolumeChange: (volume: number) => void;
}> = ({
  isMuted,
  volume,
  callDuration,
  config,
  onClose,
  onChatSelection,
  onEndCall,
  handleMicrophoneToggle,
}) => {
  const roomState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const { audioTrack } = useVoiceAssistant();
  const tracks = useTracks();
  const hasStartedMic = useRef(false);
  const micEnabledRef = useRef(false);
  const connectionAttempted = useRef(false);
  const room = useRoomContext();
  const rpcRegistered = useRef(false);

  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState("");
  const [promptMessage, setPromptMessage] = useState("");
  const resolveEmailPromise = useRef<((value: string) => void) | null>(null);

  const localTracks = tracks.filter(
    ({ participant }) => participant instanceof LocalParticipant
  );
  const localMicTrack = localTracks.find(
    ({ source }) => source === Track.Source.Microphone
  );

  useEffect(() => {
    if (localParticipant && roomState === ConnectionState.Connected) {
      console.log(`Setting microphone ${isMuted ? "muted" : "unmuted"}`);
      localParticipant.setMicrophoneEnabled(!isMuted);
      micEnabledRef.current = !isMuted;
    }
  }, [isMuted, localParticipant, roomState]);

  useEffect(() => {
    if (
      roomState === ConnectionState.Connected &&
      localParticipant &&
      !hasStartedMic.current
    ) {
      hasStartedMic.current = true;

      const timer = setTimeout(() => {
        if (!connectionAttempted.current) {
          try {
            localParticipant.setMicrophoneEnabled(true);
            micEnabledRef.current = true;
            connectionAttempted.current = true;
          } catch (error) {
            console.error("Error enabling microphone:", { error });
          }
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [roomState, localParticipant]);

  useEffect(() => {
    if (localMicTrack?.track) {
      localMicTrack.track.setVolume(volume);
    }
  }, [volume, localMicTrack]);

  useEffect(() => {
    if (localParticipant && room && !rpcRegistered.current) {
      try {
        room.registerRpcMethod("request_email", async (data) => {
          let message = "Please enter your email";
          try {
            const payload = JSON.parse(data.payload || "{}");
            message = payload.message || message;
          } catch (error) {
            console.error("Error parsing payload:", error);
          }

          setPromptMessage(message);
          setShowEmailInput(true);

          return new Promise((resolve) => {
            resolveEmailPromise.current = (result) => {
              resolve(result);
            };
          });
        });
        rpcRegistered.current = true;
      } catch (error) {
        console.error("Error registering RPC method:", error);
      }
    }

    return () => {
      if (room && rpcRegistered.current) {
        try {
          room.unregisterRpcMethod("request_email");
          rpcRegistered.current = false;
        } catch (error) {
          console.error("Error unregistering RPC method:", error);
        }
      }
    };
  }, [localParticipant, room]);

  const handleEmailSubmit = () => {
    if (resolveEmailPromise.current && email) {
      resolveEmailPromise.current(JSON.stringify({ email }));
      resolveEmailPromise.current = null;
      setShowEmailInput(false);
      setEmail("");
      setPromptMessage("");
    }
  };

  const handleCancel = () => {
    if (resolveEmailPromise.current) {
      resolveEmailPromise.current("CANCELLED");
      resolveEmailPromise.current = null;
    }
    setShowEmailInput(false);
    setEmail("");
    setPromptMessage("");
  };

  return (
    <div className="bg-white shadow-widget text-center w-[360px] rounded-[31px] flex flex-col items-center">
      <div className="p-1 w-full">
        <div className="w-full flex gap-10 flex-col items-center text-white rounded-[31px] p-4 py-3 bg-neutral-800">
          <div className="flex w-full justify-between items-center">
            <p className="font-medium">
              Call with {config.ai_agent?.agent_name}
            </p>
            <Button
              onClick={onClose}
              className="rounded-full bg-[rgba(255,255,255,0.14)] border-none size-[27px]"
              variant="outline"
              size="icon"
            >
              <X />
            </Button>
            {audioTrack && <AudioTrack trackRef={audioTrack} />}
          </div>
          <AgentAvatar config={config} className="size-[84px]" />
          <div>
            <h5 className="text-[15px]">
              {config?.ai_agent?.agent_name} {config?.ai_agent?.role?.name}
            </h5>
            <p className="opacity-50 text-[14px] mt-1">
              {roomState === ConnectionState.Connected
                ? "Connected"
                : "Connecting..."}{" "}
              • {callDuration}
            </p>
          </div>
          {showEmailInput && (
            <div className="mt-4">
              <p>{promptMessage}</p>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="mt-2"
              />
              <div className="mt-2 flex gap-2 justify-center">
                <Button onClick={handleEmailSubmit}>Submit</Button>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {config!.payment_plan?.toLowerCase() === "free" && (
        <a
          href="https://outsella.com"
          target="_blank"
          rel="noopener noreferrer"
          className="right-6 mt-2 mb-2 text-center top-1 text-xs text-gray-400 hover:underline"
        >
          Made with Outsella
        </a>
      )}

      <div className="py-3 w-full bg-gray-100 flex items-center justify-around gap-4 rounded-b-[31px]">
        <Button
          className="rounded-full"
          variant="ghost"
          onClick={handleMicrophoneToggle}
          disabled={roomState !== ConnectionState.Connected}
        >
          <FaMicrophone className={isMuted ? "opacity-50" : ""} />
          <span>Mute</span>
        </Button>

        <Button onClick={onEndCall} className="rounded-full" variant="ghost">
          <FaSquareFull className="size-[12px] text-red-500" />
          <span>End call</span>
        </Button>

        <Button
          className="rounded-full"
          variant="ghost"
          onClick={onChatSelection}
        >
          <IoChatbubble className="opacity-50" />
          <span>Chat</span>
        </Button>
      </div>
    </div>
  );
};

export default LiveKitRoomContent;
